/**
 * Light by default, dark on request — owner ruling, 1 October 2026.
 *
 * The app used to follow `prefers-color-scheme`. The adopted brand reference is
 * always light, and an owner on a dark OS opened the product to a near-black
 * page ("in the design the background is white, ours is black"). So: **light
 * for everyone by default, a toggle to dark, and the choice is remembered.**
 * Dark stays built and maintained — every token in `palette.css` keeps its dark
 * value — it is only no longer chosen *for* the user.
 *
 * ⚠️ **There is no "system" option, on purpose.** The owner chose
 * light-by-default plus a toggle. Re-adding "follow the OS" as the default is
 * exactly the regression that produced the black page; `matchMedia` is not
 * read anywhere in this module, and `theme.test.ts` stubs the OS to dark to
 * prove it.
 *
 * How it is wired:
 *
 *   localStorage `ns-theme`   "dark" | "light" (anything else, or nothing, is light)
 *   <html data-theme="dark">  set for dark only; light is the bare default
 *   palette.css               `:root[data-theme='dark']` re-points every token
 *   public/theme-boot.js      the same read, synchronously in <head>, so a
 *                             dark user never sees a light first frame
 *
 * ⚠️ **Every storage access is wrapped.** Safari's private mode, a blocked
 * third-party context and a full quota all *throw* from `localStorage`, and
 * `initTheme` runs before React mounts — an uncaught throw there is a blank
 * page, not a light one.
 */

export type Theme = 'light' | 'dark'

/** ⚠️ `public/theme-boot.js` repeats this literal — it cannot import it. */
export const THEME_STORAGE_KEY = 'ns-theme'

const root = () => document.documentElement

/** The remembered choice. Only an exact `"dark"` is dark. */
export function readStoredTheme(): Theme {
  try {
    return window.localStorage.getItem(THEME_STORAGE_KEY) === 'dark' ? 'dark' : 'light'
  } catch {
    return 'light'
  }
}

function storeTheme(theme: Theme) {
  try {
    window.localStorage.setItem(THEME_STORAGE_KEY, theme)
  } catch {
    // Private mode: the switch still holds for this visit, it just will not be
    // remembered. Nothing to tell the user — the page already changed.
  }
}

/**
 * Paint a theme: the attribute the palette keys on, and the mobile browser's
 * chrome colour.
 *
 * The chrome colours are read from the `theme-color` tag's own `data-light` /
 * `data-dark`, so the two hexes live in `index.html` (which already mirrors
 * `--ns-sunken`, with a comment saying so) and not in a third place here.
 */
export function applyTheme(theme: Theme) {
  if (theme === 'dark') root().setAttribute('data-theme', 'dark')
  else root().removeAttribute('data-theme')

  const meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]')
  const colour = meta?.dataset[theme]
  if (meta && colour) meta.content = colour
}

/** What is painted now. The DOM is the source of truth, so the boot script's work counts. */
export function getTheme(): Theme {
  return root().getAttribute('data-theme') === 'dark' ? 'dark' : 'light'
}

const listeners = new Set<() => void>()
const notify = () => listeners.forEach((listener) => listener())

/** Apply the remembered choice. Called once, at the top of `main.tsx`, before render. */
export function initTheme() {
  applyTheme(readStoredTheme())
}

/** The user's choice: paint it, remember it, and tell every toggle on the page. */
export function setTheme(theme: Theme) {
  applyTheme(theme)
  storeTheme(theme)
  notify()
}

function onStorage(event: StorageEvent) {
  // Another tab changed the choice. Following it keeps two open tabs from
  // disagreeing — and from the next reload of this one flipping unexpectedly.
  if (event.key !== THEME_STORAGE_KEY) return
  applyTheme(event.newValue === 'dark' ? 'dark' : 'light')
  notify()
}

/** For `useSyncExternalStore`. Listens across tabs while anything is subscribed. */
export function subscribeTheme(listener: () => void) {
  if (listeners.size === 0) window.addEventListener('storage', onStorage)
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
    if (listeners.size === 0) window.removeEventListener('storage', onStorage)
  }
}
